import { useMemo } from 'react';

import classNames from 'classnames';
import { isUndefined, negate } from 'lodash';
import { useAccount } from 'wagmi';

import { ONE_INCH_NATIVE_NETWORK_TOKEN_ADDRESS } from '@shared/constants/magicSwap';
import { weiToNumber } from '@shared/helpers/web3/formatters';

import {
  useGetOneInchGasPricesQuery,
  useGetOneInchQuoteQuery,
  useGetOneInchTokensQuery,
} from '@query/magicSwap';

import { DEFAULT_QUOTE_REFETCH_INTERVAL } from '@magic-swap/components/pages/MagicSwap/constants';
import useGetOneInchGasPrice from '@magic-swap/components/pages/MagicSwap/hooks/useGetOneInchGasPrice';
import useTokenPriceInUSD from '@magic-swap/components/pages/MagicSwap/hooks/useTokenPriceInUSD';
import calcTotalGasInGwei from '@magic-swap/components/pages/MagicSwap/utils/calcTotalGasInGwei';

import { useSwapExperienceStore } from '../utilities/SwapExperienceStore';
import { truncateDecimals } from '../utilities/truncateDecimals';

const SwapOperationFeeIndicator = () => {
  const { address } = useAccount();

  const { source, destination } = useSwapExperienceStore();

  const { data: tokens } = useGetOneInchTokensQuery({ networkId: source?.network?.id });

  const transactionFeeToken = useMemo(() => {
    if (!tokens || !source?.network?.id) {
      return undefined;
    }

    return tokens.find(({ id }) => id.toLowerCase() === ONE_INCH_NATIVE_NETWORK_TOKEN_ADDRESS);
  }, [source?.network?.id, tokens]);

  const { data: quote = { dstAmount: '0', gas: 0 }, isFetching: isQuoteQueryRefetching } =
    useGetOneInchQuoteQuery(
      {
        fromNetworkId: source?.network?.id,
        toNetworkId: destination?.network?.id,
        fromTokenAddress: source?.token?.id,
        toTokenAddress: destination?.token?.id,
        amount: source?.token && source?.amount ? source.amount.toString() : undefined,
        walletAddress: address,
      },
      {
        enabled:
          !!source?.network &&
          !!destination?.network &&
          !!source?.token &&
          !!destination?.token &&
          !!source.amount,
        refetchInterval: DEFAULT_QUOTE_REFETCH_INTERVAL,
      },
    );

  const { data: gasPrices } = useGetOneInchGasPricesQuery({
    networkId: source?.network?.id,
    options: { enabled: !!source?.network?.id },
  });

  const { getOneInchGasPrice } = useGetOneInchGasPrice({ gasPrices });

  const transactionFeeInTokens = useMemo(() => {
    if (!transactionFeeToken || quote?.gas === undefined) {
      return undefined;
    }

    if (quote.gas === 0) {
      return 0;
    }

    const gasPrice = getOneInchGasPrice();
    const totalGasInGwei = calcTotalGasInGwei({ gasAmount: quote.gas, gasPrice });

    return totalGasInGwei ? weiToNumber(totalGasInGwei, transactionFeeToken.attributes.decimal) : 0;
  }, [transactionFeeToken, quote?.gas, getOneInchGasPrice]);

  const { tokenPriceInUSD: transactionFeeTokenPriceInUSD } = useTokenPriceInUSD({
    network: source?.network,
    token: transactionFeeToken,
  });

  const transactionFeeInUsdFormatted = useMemo(() => {
    if (!transactionFeeInTokens) {
      return undefined;
    }

    const feeInUsd = transactionFeeInTokens * transactionFeeTokenPriceInUSD;

    return feeInUsd < 0.00001 ? '<$0.00001' : `≈$${truncateDecimals(feeInUsd, 5)}`;
  }, [transactionFeeInTokens, transactionFeeTokenPriceInUSD]);

  const transactionFeeInTokensFormatted = useMemo(() => {
    if (!transactionFeeToken) {
      return undefined;
    }

    const feeInTokens = transactionFeeInTokens ?? 0;

    return feeInTokens < 0.00000001
      ? `<0.00000001  ${transactionFeeToken.attributes.symbol}`
      : `≈${truncateDecimals(feeInTokens, 5)} ${transactionFeeToken.attributes.symbol}`;
  }, [transactionFeeInTokens, transactionFeeToken]);

  return (
    <span
      className={classNames({
        'animate-pulse-4x': isQuoteQueryRefetching,
      })}
    >
      {[
        transactionFeeInTokensFormatted,
        transactionFeeInUsdFormatted ? `(${transactionFeeInUsdFormatted})` : undefined,
      ]
        .filter(negate(isUndefined))
        .join(' ') || 'N/A'}
    </span>
  );
};

export { SwapOperationFeeIndicator };
